/**
 * Cere-design-system UI primitives for the wallet. Built with inline styles
 * over the token CSS variables (src/styles/tokens.css) to match the codebase
 * idiom while applying the brand consistently. Button/Field mirror the
 * components-buttons.html / components-inputs.html specs from the design system.
 */
import { forwardRef, useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { LogoLockup } from './Logo';
import { truncateMiddle } from './format';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'danger';
type Size = 'md' | 'sm';

const BTN_BASE: CSSProperties = {
  font: '500 16px/1 var(--font-sans)',
  letterSpacing: '0.4px',
  padding: '14px 24px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  transition:
    'background var(--dur-fast) var(--ease-standard), box-shadow var(--dur-fast) var(--ease-standard), opacity var(--dur-fast)',
  width: '100%',
};

const BTN_VARIANT: Record<Variant, CSSProperties> = {
  primary: { background: 'var(--c-violet)', color: '#fff', borderColor: 'var(--c-violet)' },
  secondary: { background: '#fff', color: 'var(--text-primary)', borderColor: 'var(--c-violet)' },
  tertiary: { background: 'transparent', color: 'var(--c-violet)', borderColor: 'transparent' },
  danger: { background: 'transparent', color: 'var(--c-red)', borderColor: 'var(--c-red)' },
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; fullWidth?: boolean }
>(function Button({ variant = 'primary', size = 'md', fullWidth = true, style, disabled, ...rest }, ref) {
  const sizeStyle: CSSProperties = size === 'sm' ? { padding: '8px 16px', fontSize: 14 } : {};
  const disabledStyle: CSSProperties = disabled
    ? { background: 'var(--bg-dim)', color: 'var(--text-muted)', borderColor: 'var(--bg-dim)', cursor: 'not-allowed' }
    : {};
  return (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        ...BTN_VARIANT[variant],
        ...sizeStyle,
        ...(fullWidth ? {} : { width: 'auto' }),
        ...disabledStyle,
        ...style,
      }}
      {...rest}
    />
  );
});

export function Card({
  children,
  style,
  padded = true,
}: {
  children: ReactNode;
  style?: CSSProperties;
  padded?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--surface-contrast)',
        border: '1px solid var(--stroke-on-low)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
        padding: padded ? 'var(--space-6)' : 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  style,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string; error?: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <span
          style={{
            font: '500 12px/1 var(--font-sans)',
            color: 'var(--text-secondary)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
      )}
      <input
        style={{
          font: '400 16px/1.5 var(--font-sans)',
          padding: '12px 14px',
          border: `1px solid ${error ? 'var(--c-red)' : 'var(--field-border)'}`,
          borderRadius: 'var(--radius-md)',
          background: '#fff',
          color: 'var(--text-primary)',
          outline: 'none',
          ...style,
        }}
        {...rest}
      />
      {(error || hint) && (
        <span
          style={{ font: '400 11px/1.4 var(--font-mono)', color: error ? 'var(--c-red)' : 'var(--text-secondary)' }}
        >
          {error || hint}
        </span>
      )}
    </label>
  );
}

export function Spinner({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        border: `2px solid color-mix(in srgb, ${color} 30%, transparent)`,
        borderTopColor: color,
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'scp-spin 0.7s linear infinite',
      }}
    />
  );
}

type ChipTone = 'neutral' | 'violet' | 'success' | 'danger' | 'mono';
const CHIP_TONE: Record<ChipTone, CSSProperties> = {
  neutral: { background: 'var(--surface-low)', color: 'var(--text-secondary)' },
  violet: { background: 'var(--c-violet-light)', color: 'var(--c-violet-dark)' },
  success: { background: 'var(--c-green-light)', color: 'var(--c-green-dark)' },
  danger: { background: 'var(--c-red-light)', color: 'var(--c-red-dark)' },
  mono: { background: 'var(--surface-low)', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' },
};

export function Chip({
  children,
  tone = 'neutral',
  style,
}: {
  children: ReactNode;
  tone?: ChipTone;
  style?: CSSProperties;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        font: '500 12px/1 var(--font-sans)',
        borderRadius: 'var(--radius-pill)',
        padding: '4px 10px',
        ...CHIP_TONE[tone],
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ textAlign: 'center', padding: 'var(--space-8) var(--space-4)', color: 'var(--text-muted)' }}>
      <p style={{ margin: 0, font: '500 15px/1.4 var(--font-sans)', color: 'var(--text-secondary)' }}>{title}</p>
      {hint && <p style={{ margin: '6px 0 0', font: '400 13px/1.4 var(--font-sans)' }}>{hint}</p>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        color: 'var(--text-secondary)',
        padding: 'var(--space-4) 0',
      }}
    >
      <Spinner size={16} color="var(--c-violet)" />
      <span style={{ font: '400 14px/1 var(--font-sans)' }}>{label}</span>
    </div>
  );
}

/**
 * A labelled value (e.g. a chain address) shown truncated in mono, with a copy
 * affordance. The full value is always copied; only the display is truncated.
 *
 * The copy button's accessible name is `Copy <label>`. Not `... address`: half
 * the call sites are not addresses (a credential ID, a raw Solana secret key,
 * the /account public key), and a screen reader calling a private key an
 * address is worse than a slightly terser name.
 */
export function CopyValue({ label, value, truncate = true }: { label: string; value: string; truncate?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can reject (permission / insecure context / lost focus); the flash just doesn't fire.
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          font: '500 12px/1 var(--font-sans)',
          color: 'var(--text-secondary)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <code
          style={{
            font: '400 14px/1.4 var(--font-mono)',
            color: 'var(--text-primary)',
            wordBreak: 'break-all',
            flex: 1,
          }}
          title={value}
        >
          {truncate ? truncateMiddle(value, 10, 8) : value}
        </code>
        <Button
          variant="secondary"
          size="sm"
          fullWidth={false}
          onClick={copy}
          aria-label={`Copy ${label}`}
          style={{ flex: '0 0 auto' }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" style={{ margin: 0, color: 'var(--c-red)', font: '400 14px/1.5 var(--font-sans)' }}>
      {children}
    </p>
  );
}

/**
 * Centered single-card layout used by the full-page wallet screens and the
 * embed popup. Header carries the SCP Wallet lockup; content is a branded card on the
 * tinted surface.
 */
export function AppShell({
  children,
  maxWidth = 440,
  footer,
}: {
  children: ReactNode;
  maxWidth?: number;
  footer?: ReactNode;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-default)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'var(--space-6)',
      }}
    >
      <header
        style={{
          width: '100%',
          maxWidth,
          display: 'flex',
          justifyContent: 'center',
          padding: 'var(--space-6) 0 var(--space-8)',
        }}
      >
        <LogoLockup height={26} />
      </header>
      <main style={{ width: '100%', maxWidth, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {children}
      </main>
      {footer && (
        <footer
          style={{ marginTop: 'var(--space-8)', color: 'var(--text-muted)', font: '400 12px/1.4 var(--font-sans)' }}
        >
          {footer}
        </footer>
      )}
    </div>
  );
}
